import { Box } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { checkIf, POLYGON_ID_KEY, SNAP_DISTANCE } from "../../utils";
import { getCenter, IOverlay, IZone } from "../../pages/Areas";

interface IProps {
  id: string;
  color: string;
  name?: string;
  coordinates: google.maps.LatLng[];
  defaultGeometry: google.maps.LatLng[];
  editable?: boolean;
  draggable?: boolean;
  updated?: boolean;
  map: google.maps.Map;
  blur?: number;
  forceBlur?: number;
  clearFocus: (forceAll?: boolean) => void;
  polygons: IOverlay[];
  zones: IZone[];
  saveZone: (zone: IZone) => void;
  setZone: (zone: IZone) => void;
  deleteZone: (id: string) => void;
  editZone: (id: string, type: "zone" | "overlay", mode: "edit" | "create") => void;
  labelsVisible?: boolean;
  labelsVisibleRef?: React.MutableRefObject<boolean>;
}

export const createContainer = (id: string) => {
  const container = document.createElement("div");
  container.className = "gmaps-container";
  container.id = id;
  return container;
};

export const createRevertButton = () => {
  const revertButton = document.createElement("button");
  revertButton.className = `gmaps-revert-button`;
  const parser = new DOMParser();
  // A marker with a custom inline SVG.
  const pinSvgString =
    '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.32843 7.96472L8.86396 10.5066L7.44975 11.9244L2.5 6.96221L7.44975 2L8.86396 3.41778L6.32843 5.95969H13.5C17.9183 5.95969 21.5 9.55042 21.5 13.9799C21.5 18.4092 17.9183 22 13.5 22H9.5V19.995H13.5C16.8137 19.995 19.5 17.3019 19.5 13.9799C19.5 10.6578 16.8137 7.96472 13.5 7.96472H6.32843Z" fill="white"/></svg>';
  const revertSvg = parser.parseFromString(pinSvgString, "image/svg+xml").documentElement;
  revertButton.appendChild(revertSvg);
  return revertButton;
};

export const createLabel = (name?: string, isVisible: boolean = true) => {
  const label = document.createElement("div");
  label.className = `gmaps-label-tag${!isVisible ? " hidden" : ""}`;
  label.textContent = name || "Please enter name";
  return label;
};

export const createButtonContainer = () => {
  const buttonContainer = document.createElement("div");
  buttonContainer.className = "gmaps-button-container";
  return buttonContainer;
};

export const createInput = (id: string) => {
  const input = document.createElement("input");
  input.className = "gmaps-input";
  input.id = `input-${id}`;
  input.type = "text";
  input.placeholder = "Enter area name";
  return input;
};

export const createSaveButton = () => {
  const saveButton = document.createElement("button");
  saveButton.className = `gmaps-save-button`;
  saveButton.textContent = "Save";
  return saveButton;
};

export default function Polygon(props: IProps) {
  const [polygon, setPolygon] = useState<google.maps.Polygon>();
  const [marker, setMarker] = useState<google.maps.marker.AdvancedMarkerElement>();
  const currentZones = useRef(props.zones);
  const currentPolygons = useRef(props.polygons);

  // Update refs when props change
  useEffect(() => {
    currentZones.current = props.zones;
  }, [props.zones]);

  useEffect(() => {
    currentPolygons.current = props.polygons;
  }, [props.polygons]);

  // Remove selectPolygon function since all polygons are always selected
  // function selectPolygon(polygon: google.maps.Polygon) { ... }

  const listener = useCallback(
    (index: any) => {
      if (!polygon) return;
      if (!polygon.get("isSaving")) {
        // Removed dragging.current check
        // Prevent infinite recursion
        if (polygon.get("isProcessingSnap")) {
          return;
        }

        var snapPoint;
        const path = polygon.getPath();
        const activePoint = path.getAt(index);

        if (activePoint) {
          // Get the last known position for this vertex
          const lastPosition = polygon.get(`lastPosition_${index}`);

          // Check if the user actually moved the point (not just a programmatic change)
          let userMoved = true;
          if (lastPosition) {
            const distanceMoved = google.maps.geometry.spherical.computeDistanceBetween(activePoint, lastPosition);
            userMoved = distanceMoved > 5; // User must move at least 5 meters for it to count
          }

          // Only process snapping if the user actually moved the point
          if (userMoved) {
            const snappableZones = currentZones.current
              .filter((p) => p.id !== polygon.get(POLYGON_ID_KEY))
              .flatMap((p) => p.coordinates || []);

            const snappableOverlays = currentPolygons.current
              .filter((p) => p.get(POLYGON_ID_KEY) !== polygon.get(POLYGON_ID_KEY))
              .flatMap((p) => p.getPath().getArray());

            // Find all potential snap points
            const snappables = [...snappableZones, ...snappableOverlays]
              .map((point) => ({
                distance: Math.abs(
                  google.maps.geometry.spherical.computeDistanceBetween(point as google.maps.LatLng, activePoint)
                ),
                point: point,
              }))
              .filter((d) => d.distance < SNAP_DISTANCE)
              .sort((x, y) => x.distance - y.distance);

            if (snappables.length > 0) {
              snapPoint = snappables[0].point;
            }

            // Toggle behavior: if we're within snap distance of any point
            if (!!snapPoint) {
              const currentIsSnapped = polygon.get("isSnapped");

              if (!currentIsSnapped) {
                // Not currently snapped, so snap to the closest point
                polygon.set("isProcessingSnap", true);
                path.setAt(index, snapPoint as google.maps.LatLng);
                polygon.set("isSnapped", true);
                polygon.set(`lastPosition_${index}`, snapPoint);
                polygon.set("isProcessingSnap", false);

                // Bring polygon to front when snapping
                const highZIndex = 1000 + (Date.now() % 1000); // Generate a high unique zIndex
                polygon.setOptions({ zIndex: highZIndex });
              } else {
                // Currently snapped, so unsnap (stay at current user position)
                polygon.set("isSnapped", false);
                polygon.set(`lastPosition_${index}`, activePoint);
              }
            } else {
              // No snap point found, reset snap state and remember position
              polygon.set("isSnapped", false);
              polygon.set(`lastPosition_${index}`, activePoint);
            }
          } else {
            // Update the last position even for programmatic changes
            polygon.set(`lastPosition_${index}`, activePoint);
          }
        }

        if (marker) {
          marker.position = getCenter(polygon);
        }

        // Update state immediately - this is required for preserving polygon states
        const zone = currentZones.current.find((z) => z.id === polygon.get(POLYGON_ID_KEY));

        const newCoordinates = polygon
          .getPath()
          .getArray()
          .map((coord) => ({
            lat: coord.lat(),
            lng: coord.lng(),
          }));
        const referenceCoordinates = zone?.defaultGeometry?.map((coord) => ({
          lat: coord.lat(),
          lng: coord.lng(),
        }));

        const isReferenceCoordinates = newCoordinates.every((coord) => {
          return referenceCoordinates?.some((refCoord) => refCoord.lat === coord.lat && refCoord.lng === coord.lng);
        });

        const isSameCoordinates = isReferenceCoordinates && newCoordinates.length === referenceCoordinates?.length;

        if (zone) {
          props.setZone({
            ...zone,
            coordinates: polygon.getPath().getArray(),
            updated: !isSameCoordinates,
          });

          const container = marker?.content as HTMLElement;
          const buttonContainer = container.querySelector(".gmaps-button-container");
          const revertButton = buttonContainer?.querySelector(".gmaps-revert-button");
          const saveButton = buttonContainer?.querySelector(".gmaps-save-button");

          if (revertButton) {
            revertButton.classList.add("gmaps-revert-button-active");
          }
          if (saveButton) {
            saveButton.classList.add("gmaps-save-button-active");
          }
        }
      }
    },
    [polygon, marker, props.setZone]
  );

  const findZone = useCallback((id: string) => {
    return currentZones.current.find((z) => z.id === id);
  }, []);

  useEffect(() => {
    if (!checkIf(props.map)) return;
    let polygon = new google.maps.Polygon({
      map: props.map,
      paths: props.coordinates,
      strokeColor: props.color,
      fillColor: props.color,
      editable: true, // Always editable to show edges
      draggable: false, // Never draggable
    });
    polygon.set(POLYGON_ID_KEY, props.id);
    polygon.set("isSelected", true); // Always selected

    const container = createContainer(props.id);
    const label = createLabel(props.name, props.labelsVisible);
    const buttonContainer = createButtonContainer();
    const revertButton = createRevertButton();
    const saveButton = createSaveButton();

    revertButton.addEventListener("click", () => {
      const zone = currentZones.current.find((z) => z.id === polygon.get(POLYGON_ID_KEY));
      if (zone?.defaultGeometry) {
        polygon.setPaths(zone.defaultGeometry);
        marker.position = getCenter(polygon);
        container.querySelector(".gmaps-revert-button")?.classList.remove("gmaps-revert-button-active");
        container.querySelector(".gmaps-save-button")?.classList.remove("gmaps-save-button-active");

        props.setZone({
          ...zone,
          coordinates: polygon.getPath().getArray(),
          updated: false,
        });
      }
    });

    saveButton.addEventListener("click", () => {
      container.querySelector(".gmaps-revert-button")?.classList.remove("gmaps-revert-button-active");
      container.querySelector(".gmaps-save-button")?.classList.remove("gmaps-save-button-active");

      const zone = currentZones.current.find((z) => z.id === polygon.get(POLYGON_ID_KEY));
      if (zone) {
        // Set a flag on the polygon to indicate it's being saved
        polygon.set("isSaving", true);
        props.saveZone({ ...zone, coordinates: polygon.getPath().getArray() });
        // Reset the flag after save
        setTimeout(() => {
          polygon.set("isSaving", false);
        }, 200);
      }
    });

    container.appendChild(label);
    container.appendChild(buttonContainer);
    buttonContainer.appendChild(revertButton);
    // HIDE SAVE BUTTON
    //buttonContainer.appendChild(saveButton);
    const center = getCenter(polygon);

    let marker = new google.maps.marker.AdvancedMarkerElement({
      map: props.map,
      position: center,
      content: container,
    });

    setMarker(marker);

    polygon.addListener("click", () => {
      // No selection logic needed since all polygons are always selected
    });

    polygon.addListener("rightclick", (ev: any) => {
      if (ev.vertex != null) {
        // check if at least 3 points are left
        if (polygon.getPath().getArray().length <= 3) {
          return;
        }
        polygon.getPath().removeAt(ev.vertex);
        const zone = findZone(polygon.get(POLYGON_ID_KEY));

        if (zone) {
          props.setZone({
            ...zone,
            updated: true,
            coordinates: polygon.getPath().getArray(),
          });
        }
      } else {
        props.editZone(polygon.get(POLYGON_ID_KEY), "zone", "edit");
      }
    });

    // Remove drag listeners since polygons are not draggable
    // polygon.addListener("dragstart", ...);
    // polygon.addListener("dragend", ...);
    // polygon.addListener("drag", ...);

    // Add hover effects for labels when they are hidden
    polygon.addListener("mouseover", () => {
      // Use the ref to get the current state, fallback to props if ref not available
      const currentLabelsVisible = props.labelsVisibleRef ? props.labelsVisibleRef.current : props.labelsVisible;
      if (currentLabelsVisible === false) {
        const container = marker?.content as HTMLElement;
        const label = container?.querySelector(".gmaps-label-tag.hidden");
        if (label) {
          label.classList.add("polygon-hover");
        }
      }
    });

    polygon.addListener("mouseout", () => {
      // Always remove the hover class regardless of current state
      const container = marker?.content as HTMLElement;
      const label = container?.querySelector(".gmaps-label-tag");
      if (label) {
        label.classList.remove("polygon-hover");
      }
    });

    setPolygon(polygon);

    return () => {
      polygon.setMap(null);
      if (marker) {
        marker.map = null;
      }
    };
  }, [props.map, findZone]);

  // Remove blur effects since polygons are always selected
  // useEffect(() => {
  //   if (props.blur && polygon) {
  //     ...
  //   }
  // }, [props.blur, polygon]);

  // useEffect(() => {
  //   if (props.forceBlur && polygon) {
  //     ...
  //   }
  // }, [props.forceBlur, polygon]);

  useEffect(() => {
    const container = marker?.content as HTMLElement;
    const buttonContainer = container?.querySelector(".gmaps-button-container");
    const revertButton = buttonContainer?.querySelector(".gmaps-revert-button");
    const saveButton = buttonContainer?.querySelector(".gmaps-save-button");
    if (props.updated) {
      if (revertButton) {
        revertButton.classList.add("gmaps-revert-button-active");
      }
      if (saveButton) {
        saveButton.classList.add("gmaps-save-button-active");
      }
    } else {
      if (revertButton) {
        revertButton.classList.remove("gmaps-revert-button-active");
      }
      if (saveButton) {
        saveButton.classList.remove("gmaps-save-button-active");
      }
    }
  }, [props.updated, marker]);

  useEffect(() => {
    if (!checkIf(props.map)) return;
    if (!polygon) return;

    const path = polygon.getPath();
    path.unbindAll();
    path.addListener("set_at", listener);
    path.addListener("insert_at", listener);
    path.addListener("remove_at", listener);

    // Ensure polygons are always editable and never draggable
    polygon.setEditable(true);
    polygon.setDraggable(false);
  }, [polygon, props.map, listener]);

  useEffect(() => {
    if (props.name) {
      const container = marker?.content as HTMLElement;
      const label = container?.querySelector(".gmaps-label-tag");
      if (label) {
        label.textContent = props.name;
      }
    }
  }, [props.name, marker]);

  // Effect to handle label visibility changes
  useEffect(() => {
    const container = marker?.content as HTMLElement;
    const label = container?.querySelector(".gmaps-label-tag");
    if (label) {
      if (props.labelsVisible === false) {
        label.classList.add("hidden");
      } else {
        label.classList.remove("hidden");
      }
    }
  }, [props.labelsVisible, marker]);

  return null;
}
